import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell, ErrorNote } from "@/pages/Login";
import { CodeBlock } from "@/components/Primitives";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
    } catch (err) {
      setError(errMsg(err, "Unable to request reset"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      subtitle="We generate a one-hour reset token."
      footer={<Link to="/login" className="text-primary hover:underline" data-testid="link-back-login">Back to sign in</Link>}
    >
      {result ? (
        <div className="space-y-4" data-testid="forgot-result">
          <p className="text-sm text-[hsl(var(--success))] border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/5 px-3 py-2">
            {result.message}
          </p>
          {result.resetToken && (
            <>
              <p className="text-xs text-muted-foreground">
                No email provider is configured in this MVP, so the token is shown here:
              </p>
              <CodeBlock testId="reset-token-block" label="reset token" code={result.resetToken} />
              <Link to={`/reset-password?token=${result.resetToken}`} data-testid="continue-reset">
                <Button className="w-full rounded-none">Continue to reset</Button>
              </Link>
            </>
          )}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
          <ErrorNote message={error} />
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none" />
          </div>
          <Button type="submit" data-testid="forgot-submit" disabled={busy} className="w-full rounded-none gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Request reset
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(errMsg(err, "Unable to reset password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Choose a new password" subtitle="Tokens expire after one hour.">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
        <ErrorNote message={error} />
        <div className="space-y-2">
          <Label htmlFor="token">Reset token</Label>
          <Input id="token" data-testid="reset-token" required value={token} onChange={(e) => setToken(e.target.value)} className="rounded-none font-mono text-xs" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">New password</Label>
          <Input id="password" data-testid="reset-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none" />
        </div>
        <Button type="submit" data-testid="reset-submit" disabled={busy} className="w-full rounded-none gap-2">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Set new password
        </Button>
      </form>
    </AuthShell>
  );
}
